import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { Truck, ShoppingBag, MessageCircle } from 'lucide-react';

const OrderSection = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const deliveryPlatforms = [
    {
      name: 'Wolt',
      url: 'https://order.site/cobarci/tr/deu/berlin/restaurant/corbaci-sf?no_universal_links=true',
      logo: '/images/brands/wolt.svg',
      color: 'from-blue-500 to-blue-600'
    },
    {
      name: 'Uber Eats',
      url: 'https://www.ubereats.com/de/store/corbaci-authentische-turkische-kuche/Z4L9pQSxVnyHd6TEiOAjOQ',
      logo: '/images/brands/ubereats.svg',
      color: 'from-green-500 to-green-600'
    },
    {
      name: 'Lieferando',
      url: 'https://www.lieferando.de/speisekarte/corbaci-10627',
      logo: '/images/brands/lieferando.svg',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section id="order" className="py-20 bg-gradient-to-b from-gray-900 to-black min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 pt-20">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Crimson Text', serif" }}>
            {t.order_title}
          </h2>
          <p className="text-xl text-white/80">
            {t.order_subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Delivery */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <Truck className="w-8 h-8 text-[#d4af37]" />
              <div>
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Crimson Text', serif" }}>
                  {t.delivery_title}
                </h3>
                <p className="text-white/70">{t.delivery_subtitle}</p>
              </div>
            </div>

            <div className="space-y-4">
              {deliveryPlatforms.map((platform, index) => (
                <a
                  key={index}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block p-6 bg-white/5 rounded-xl border border-white/10 hover:border-[#d4af37]/50 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-28 h-14 bg-white rounded-lg flex items-center justify-center px-3">
                        <img src={platform.logo} alt={platform.name} className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="text-white/60 text-sm">{t.order_now}</p>
                      </div>
                    </div>
                    <div className="text-[#d4af37] group-hover:translate-x-2 transition-transform duration-200 text-2xl">
                      →
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right Side - Pickup */}
          <div className="bg-gradient-to-br from-[#d4af37]/20 to-[#f0c24d]/10 backdrop-blur-sm rounded-2xl p-8 border border-[#d4af37]/30">
            <div className="flex items-center gap-3 mb-6">
              <ShoppingBag className="w-8 h-8 text-[#d4af37]" />
              <div>
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Crimson Text', serif" }}>
                  {t.pickup_title}
                </h3>
                <p className="text-white/70">{t.pickup_subtitle}</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-black/30 rounded-xl p-6 border border-[#d4af37]/20">
                <h4 className="text-lg font-semibold text-white mb-4">Kaiser-Friedrich-Straße 49</h4>
                <p className="text-white/70 mb-2">10627 Berlin, Charlottenburg</p>
                <p className="text-[#d4af37] font-semibold mb-4">{t.hours_value}</p>
                
                <div className="space-y-3">
                  <a
                    href="https://wa.me/493089542916"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-green-500 text-white px-6 py-4 rounded-lg font-bold hover:bg-green-600 transition-all duration-300 transform hover:scale-105"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {t.order_whatsapp}
                  </a>
                  
                  <a
                    href="tel:+493089542916"
                    className="block w-full bg-white/10 text-white text-center px-6 py-4 rounded-lg font-bold border-2 border-white/20 hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105"
                  >
                    📞 +49 30 8954 2916
                  </a>
                </div>
              </div>

              <div className="text-center text-white/60 text-sm">
                <p>{language === 'de' ? 'Rufen Sie an oder bestellen Sie per WhatsApp zur Abholung' : language === 'en' ? 'Call or order via WhatsApp for pickup' : 'Gel al için telefon edin veya WhatsApp ile sipariş verin'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderSection;
